// src/components/institution/sections/InstitutionHeroSection.jsx
import React from 'react';
import { ChevronRight, Users, Award, Calendar, ArrowDown } from 'lucide-react';

// Utility function to convert hex to rgba
const hexToRgba = (hex, opacity = 1) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return `rgba(30, 64, 175, ${opacity})`; // fallback
  
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const InstitutionHeroSection = ({ data, settings, styling, onSectionChange }) => {
  const primaryColor = data?.primaryColor || '#1e40af';
  const heroImage = data?.heroImageUrl || data?.coverImageUrl;
  const overlayOpacity = settings?.overlayOpacity || 0.6; // Default 40% opacity
  
  // Create dynamic overlay colors
  const overlayColor = hexToRgba(primaryColor, overlayOpacity);
  
  // Create background style
  const backgroundStyle = {
    backgroundColor: primaryColor,
    background: heroImage 
      ? `linear-gradient(${overlayColor}, ${overlayColor}), url(${heroImage})`
      : primaryColor,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  };

  return (
    <section 
      className="relative min-h-[90vh] flex items-center justify-center px-4 py-15"
      style={backgroundStyle}
      id="section-hero"
    >
      <div className="relative max-w-7xl mx-auto text-center text-white z-10">
        
        {/* Institution Logo */}
        {data?.logoUrl && (
          <div className="mb-6">
            <img 
              src={data.logoUrl} 
              alt={data.botName}
              className="h-28 w-28 md:h-36 md:w-36 mx-auto rounded-full object-cover shadow-2xl border-4 border-white/20"
            />
          </div>
        )}

        {/* Main Heading */}
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6 text-white drop-shadow-lg">
          {data?.botName || 'Educational Institution'}
        </h1>

        {/* Tagline */}
        {settings?.tagline && (
          <p className="text-xl md:text-2xl lg:text-3xl text-white/90 mb-8 max-w-4xl mx-auto font-light">
            {settings.tagline}
          </p>
        )}

        {/* Description */}
        {data?.description && (
          <p className="text-lg md:text-xl text-white/80 mb-12 max-w-4xl mx-auto leading-relaxed">
            {data.description}
          </p>
        )}

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
          <button
            onClick={() => onSectionChange('about')}
            className="bg-white text-gray-900 px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/90 transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Learn More
          </button>
          
          <button
            onClick={() => onSectionChange('contact')}
            className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-gray-900 transition-all duration-300 transform hover:scale-105"
          >
            Get In Touch
          </button>
        </div>

        {/* Quick Stats */}
        {(settings?.studentCount || settings?.programCount || settings?.staffCount) && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 max-w-4xl mx-auto">
            {settings?.studentCount && (
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold mb-2 text-white">
                  {settings.studentCount}
                </div>
                <div className="text-white/80 uppercase tracking-wider text-sm">Students</div>
              </div>
            )}
            
            {settings?.programCount && (
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold mb-2 text-white">
                  {settings.programCount}
                </div>
                <div className="text-white/80 uppercase tracking-wider text-sm">Programs</div>
              </div>
            )}
            
            {settings?.staffCount && (
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold mb-2 text-white">
                  {settings.staffCount}
                </div>
                <div className="text-white/80 uppercase tracking-wider text-sm">Faculty</div>
              </div>
            )}
          </div>
        )}

        {/* Scroll Down Indicator */}
        <div 
          className="absolute bottom-0 left-1/2 transform -translate-x-1/2 cursor-pointer animate-bounce"
          onClick={() => onSectionChange('about')}
        >
          <div className="w-12 h-12 rounded-full border-2 border-white/50 flex items-center justify-center hover:border-white transition-colors">
            <ArrowDown className="w-5 h-5 text-white" />
          </div>
        </div>
      </div>

      {/* Background Pattern Overlay (optional) */}
      <div className="absolute inset-0 opacity-10">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}
        />
      </div>
    </section>
  );
};